import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ProductCard from '../components/shared/ProductCard';
import RecipeCard from '../components/shared/RecipeCard';
import { products, recipes } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiPlay, FiAward, FiShield, FiSmile } from 'react-icons/fi';

export default function Home() {
  return (
    <div className="overflow-x-hidden">
      
      {/* REFINED HERO: INTENTIONAL SPACING & VISUAL STORYTELLING */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-4 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-bloom-sand rounded-[3rem] sm:rounded-[4rem] overflow-hidden min-h-[600px] lg:min-h-[720px] flex items-center">
            
            {/* Background Decorative Element */}
            <div className="absolute top-0 right-0 w-1/2 h-full bg-bloom-mint/10 skew-x-[-15deg] translate-x-20 hidden lg:block"></div>
            
            <div className="relative z-10 w-full flex flex-col lg:flex-row items-center px-8 sm:px-16 py-16 gap-12">
              <div className="w-full lg:w-1/2 text-center lg:text-left">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-8">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bloom-coral opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-bloom-coral"></span>
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500">New Spring Workspace</span>
                  </div>
                  
                  <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl font-bold text-bloom-charcoal leading-[1.05] mb-8">
                    Lunch, <br />
                    <span className="text-bloom-coral italic">Redefined.</span>
                  </h1>
                  
                  <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                    Modular prep sets and chef-crafted recipe kits designed to turn your lunch break into a daily celebration.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5">
                    <Link to="/shop" className="w-full sm:w-auto bg-bloom-charcoal text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-bloom-coral transition-all shadow-xl shadow-bloom-charcoal/10">
                      Explore the Shop
                    </Link>
                    <button className="flex items-center gap-3 font-bold group">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                        <SafeIcon icon={FiPlay} className="text-bloom-coral ml-1" />
                      </div>
                      <span className="text-gray-600 group-hover:text-bloom-charcoal transition-colors">See the Experience</span>
                    </button>
                  </div>
                </motion.div>
              </div>

              <div className="w-full lg:w-1/2 relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="relative"
                >
                  <div className="aspect-[4/5] rounded-4xl overflow-hidden shadow-2xl rotate-2">
                    <img 
                      src="https://images.unsplash.com/photo-1512058564366-18510be2db19?auto=format&fit=crop&q=80&w=1200" 
                      className="w-full h-full object-cover"
                      alt="Healthy Bento Prep"
                    />
                  </div>
                  {/* Floating Card */}
                  <div className="absolute -bottom-10 -left-10 bg-white p-6 rounded-3xl shadow-2xl hidden sm:block max-w-[240px]">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-bloom-yellow flex items-center justify-center">
                        <SafeIcon icon={FiAward} className="text-xl" />
                      </div>
                      <span className="font-bold text-sm">Best of 2024</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">"The most thoughtful bento system I've ever used. Truly life-changing."</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CURATED COLLECTIONS - GRID LAYOUT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-xl">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-bloom-charcoal mb-4">Curated Essentials</h2>
            <p className="text-gray-500 text-lg">Every component is engineered for durability, safety, and aesthetic joy.</p>
          </div>
          <Link to="/shop" className="group flex items-center gap-2 font-bold text-bloom-coral border-b-2 border-transparent hover:border-bloom-coral transition-all pb-1">
            Browse All Products <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
          {products.slice(0, 3).map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* LIFESTYLE SECTION: THE RECIPE HUB */}
      <section className="bg-bloom-charcoal py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="text-bloom-mint font-black uppercase tracking-widest text-xs mb-6 block">The Weekly Digest</span>
              <h2 className="font-heading text-4xl sm:text-6xl font-bold text-white mb-8 leading-tight">
                Stop Guessing. <br />
                <span className="text-bloom-mint">Start Prepping.</span>
              </h2>
              <p className="text-gray-400 text-lg mb-10 max-w-md">
                Our recipe-first approach means you buy what you need. Shop the kit, get the guide, and master the art of the 15-minute lunch.
              </p>
              <div className="space-y-6">
                {[
                  { icon: FiShield, title: 'BPA-Free Promise', desc: 'Medical-grade materials only.' },
                  { icon: FiSmile, title: 'Kid-Approved', desc: 'Tested by the toughest critics.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-bloom-mint shrink-0">
                      <SafeIcon icon={item.icon} className="text-2xl" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid gap-6">
              {recipes.slice(0, 2).map((recipe, i) => (
                <motion.div 
                  key={recipe.id}
                  whileHover={{ x: 10 }}
                  className="bg-white/5 backdrop-blur-md rounded-4xl p-4 flex items-center gap-6 group cursor-pointer border border-white/10"
                >
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-3xl overflow-hidden shrink-0">
                    <img src={recipe.image} className="w-full h-full object-cover" alt={recipe.title} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-bloom-mint uppercase tracking-widest block mb-1">{recipe.difficulty} • {recipe.calories}</span>
                    <h3 className="text-white font-heading text-xl sm:text-2xl font-bold mb-2">{recipe.title}</h3>
                    <span className="text-gray-400 text-sm font-medium flex items-center gap-2">
                      Shop the Look <SafeIcon icon={FiArrowRight} />
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM GRID STYLE SOCIAL PROOF */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl font-bold text-bloom-charcoal mb-4">Joined by 50,000+ Bloomers</h2>
          <p className="text-gray-500">Tag #BentoBloom for a chance to be featured in our workspace gallery.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {[
            'https://images.unsplash.com/photo-1546069901-ba9599a7e63c',
            'https://images.unsplash.com/photo-1490645935967-10de6ba17061',
            'https://images.unsplash.com/photo-1543362906-acfc16c67564',
            'https://images.unsplash.com/photo-1498837167922-41c46b3f6162'
          ].map((img, i) => (
            <div key={i} className="aspect-square rounded-3xl overflow-hidden relative group">
              <img src={`${img}?auto=format&fit=crop&q=80&w=600`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Social" />
              <div className="absolute inset-0 bg-bloom-coral/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}